import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from "react";
import { useListarPlantaoVendas } from "services/api/Common_PlantaoVendas/listar-plantao-vendas";

interface PDV {
  idPdv: string;
  nome: string;
  idEmpreendimento: string;
  nomeEmpreendimento: string;
}

interface PDVOption {
  value: string | undefined;
  label: string | undefined;
  idEmpreendimento: string | undefined;
  nomeEmpreendimento: string | undefined;
}

interface PlantaoVendasContextData {
  plantaoVendasData: PDV[];
  plantaoVendasStatus: string;
  plantaoVendas: PDVOption | null;
  setPlantaoVendas: (value: PDVOption | null) => void;
  formattedPlantaoVendas: PDVOption[];
}

interface PlantaoVendasProviderProps {
  children: ReactNode;
}

const PlantaoVendasContext = createContext<PlantaoVendasContextData>(
  {} as PlantaoVendasContextData
);

export function PlantaoVendasProvider({
  children,
}: PlantaoVendasProviderProps) {
  const { data: plantaoVendasData, status: plantaoVendasStatus } =
    useListarPlantaoVendas();
  const [plantaoVendas, setPlantaoVendas] = useState<PDVOption | null>(null);

  const formattedPlantaoVendas = useMemo(() => {
    if (!plantaoVendasData) return [];

    return plantaoVendasData.map((pdv: PDV) => ({
      value: pdv.idPdv,
      label: pdv.nome,
      idEmpreendimento: pdv.idEmpreendimento,
      nomeEmpreendimento: pdv.nomeEmpreendimento,
    }));
  }, [plantaoVendasData]);

  const contextValue = useMemo(
    () => ({
      plantaoVendasData,
      plantaoVendasStatus,
      plantaoVendas,
      setPlantaoVendas,
      formattedPlantaoVendas,
    }),
    [
      plantaoVendasData,
      plantaoVendasStatus,
      plantaoVendas,
      formattedPlantaoVendas,
    ]
  );

  return (
    <PlantaoVendasContext.Provider value={contextValue}>
      {children}
    </PlantaoVendasContext.Provider>
  );
}

export function usePlantaoVendas() {
  const context = useContext(PlantaoVendasContext);

  if (!context) {
    throw new Error(
      "usePlantaoVendas deve ser usado dentro de um PlantaoVendasProvider"
    );
  }

  return context;
}
